import React, { createContext, useEffect, useMemo, useState } from 'react';
import { LOCALE_STORAGE_KEY, MESSAGES, SUPPORTED_LOCALES } from './messages';

const I18nContext = createContext(null);

function detectLocale() {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch (_) {}

  const nav = typeof navigator !== 'undefined' ? (navigator.languages || [navigator.language]) : [];
  const match = nav.find(lang => typeof lang === 'string' && lang.toLowerCase().startsWith('ru'));
  return match ? 'ru' : 'en';
}

function getMessage(messages, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), messages);
}

function interpolate(template, vars) {
  if (typeof template !== 'string') return template;
  return template.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
    const value = vars?.[key];
    return value === undefined || value === null ? '' : String(value);
  });
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(detectLocale);

  useEffect(() => {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch (_) {}
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => {
    const messages = MESSAGES[locale] || MESSAGES.en;
    return {
      locale,
      setLocale: (nextLocale) => {
        if (SUPPORTED_LOCALES.includes(nextLocale)) setLocaleState(nextLocale);
      },
      t: (path, vars = {}) => {
        const fallback = getMessage(MESSAGES.en, path);
        const current = getMessage(messages, path);
        return interpolate(current ?? fallback ?? path, vars);
      },
      formatDate: (value, options) => new Intl.DateTimeFormat(locale, options).format(new Date(value)),
      formatNumber: (value, options) => new Intl.NumberFormat(locale, options).format(value),
      dir: locale === 'ru' ? 'ltr' : 'ltr',
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = React.useContext(I18nContext);
  if (context) return context;

  const locale = detectLocale();
  const messages = MESSAGES[locale] || MESSAGES.en;
  return {
    locale,
    setLocale: () => {},
    t: (path, vars = {}) => {
      const fallback = getMessage(MESSAGES.en, path);
      const current = getMessage(messages, path);
      return interpolate(current ?? fallback ?? path, vars);
    },
    formatDate: (value, options) => new Intl.DateTimeFormat(locale, options).format(new Date(value)),
    formatNumber: (value, options) => new Intl.NumberFormat(locale, options).format(value),
    dir: 'ltr',
  };
}

